import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deletePracticeMeeting } from '../../api/meetingApi';
import { getMyPracticeMeetings, getMyTrainingPlans } from '../../api/userApi';
import CompletedTrainingModal from '../../components/CompletedTrainingModal/CompletedTrainingModel';
import SchedulePickerModal from '../../components/SchedulePickerModal/SchedulePickerModal';
import { useToast } from '../../components/Toaster/use-toast';
import {
  clearCompletedPlan,
  closeScheduleModal,
  openScheduleModal,
  saveScheduleThunk,
  setCompletedPlan,
  setScheduleDate,
  setScheduleTime,
} from '../../store/trainingSlice';
import { canUseTrainingPlans } from '../../utils/billingPlans';
import { toISODate } from '../../utils/schedule';
import UserMeetingsIsland from './UserMeetingsIsland/UserMeetingsIsland';
import UserTrainingPlansIsland from './UserTrainingPlansIsland/UserTrainingPlansIsland';
import './UserActivity.css';

const MEETING_PAGE_SIZE = 5;
const TRAINING_PAGE_SIZE = 10;

export default function UserActivity({ view }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = useSelector((state) => state.user?.data) || {};
  const completedPlan = useSelector((state) => state.training.completedPlan);
  const { scheduler, lastScheduled } = useSelector((state) => state.training);

  const [meetingPage, setMeetingPage] = useState(1);
  const [meetingPages, setMeetingPages] = useState(1);
  const [meetings, setMeetings] = useState([]);
  const [meetingRefreshKey, setMeetingRefreshKey] = useState(0);

  const [trainingPage, setTrainingPage] = useState(1);
  const [trainingData, setTrainingData] = useState({
    items: [],
    pages: 1,
    programProgress: {},
  });
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [dateRange, setDateRange] = useState('all');

  useEffect(() => {
    if (view !== 'practice') return undefined;

    let active = true;
    getMyPracticeMeetings({ page: meetingPage, limit: MEETING_PAGE_SIZE })
      .then((data) => {
        if (!active) return;
        setMeetings(data.items || []);
        setMeetingPages(data.pages || 1);
      })
      .catch((error) => console.error('Error loading practice meetings', error));

    return () => {
      active = false;
    };
  }, [meetingPage, meetingRefreshKey, view]);

  useEffect(() => {
    if (view !== 'training') return undefined;

    let active = true;
    setTrainingLoading(true);
    getMyTrainingPlans({
      page: trainingPage,
      limit: TRAINING_PAGE_SIZE,
      program: selectedProgram || undefined,
      industry: selectedIndustry || undefined,
    })
      .then((data) => {
        if (active) setTrainingData(data);
      })
      .catch((error) => console.error('Error loading training plans', error))
      .finally(() => {
        if (active) setTrainingLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedIndustry, selectedProgram, trainingPage, view]);

  useEffect(() => {
    if (!lastScheduled || view !== 'training') return;
    setTrainingData((current) => ({
      ...current,
      items: current.items.map((item) =>
        item.id === lastScheduled.planId
          ? { ...item, scheduledTime: lastScheduled.scheduledTime }
          : item
      ),
    }));
  }, [lastScheduled, view]);

  const trainingPlansEnabled = canUseTrainingPlans(
    user?.billing?.plan?.code || user?.entitlements?.planCode || '',
    user?.billing?.plan?.name || user?.entitlements?.planName || ''
  );

  const availableIndustries = useMemo(
    () => Array.from(
      new Set(
        trainingData.items
          .map((plan) => plan.meetingData?.industry)
          .filter(Boolean)
      )
    ).sort(),
    [trainingData.items]
  );

  const handleDeleteMeeting = async (id) => {
    try {
      await deletePracticeMeeting(id);
      const wasLastOnPage = meetings.length === 1;
      setMeetings((current) => current.filter((meeting) => meeting.id !== id));

      if (wasLastOnPage && meetingPage > 1) {
        setMeetingPage((page) => page - 1);
      } else {
        setMeetingRefreshKey((key) => key + 1);
      }

      toast({
        title: 'Practice meeting deleted',
        description: 'The meeting has been successfully removed.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete the meeting. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : '—');
  const selectedPlan = scheduler.plan;

  return (
    <main className="dashboard-main">
      {view === 'practice' ? (
        <UserMeetingsIsland
          paginatedMeetings={meetings}
          fmt={formatDate}
          onDeleteMeeting={handleDeleteMeeting}
          onResumeMeeting={(meeting) => {
            if (meeting.id === 'new') {
              navigate('/conversation-type');
              return;
            }
            navigate(`/meeting/${meeting.id}`, { state: { meetingData: meeting } });
          }}
          meetPage={meetingPage}
          meetPages={meetingPages}
          onPageChange={setMeetingPage}
        />
      ) : (
        <UserTrainingPlansIsland
          items={trainingData.items}
          programProgress={trainingData.programProgress}
          selectedProgram={selectedProgram}
          setSelectedProgram={(program) => {
            setSelectedProgram(program);
            setSelectedIndustry('');
            setTrainingPage(1);
          }}
          availableIndustries={availableIndustries}
          selectedIndustry={selectedIndustry}
          setSelectedIndustry={(industry) => {
            setSelectedIndustry(industry);
            setTrainingPage(1);
          }}
          dateRange={dateRange}
          setDateRange={setDateRange}
          fmt={formatDate}
          onPlanAction={(plan) => {
            if (!trainingPlansEnabled) {
              navigate('/pricing');
              return;
            }

            const meeting = plan.meetingData;
            if (plan.status === 'Completed') {
              dispatch(setCompletedPlan({ plan, meeting }));
              return;
            }

            navigate(`/meeting/${meeting.id}`, {
              state: { meetingData: meeting, trainingPlanId: plan.id },
            });
          }}
          onOpenSchedule={(plan) => dispatch(openScheduleModal(plan))}
          onEmptyAction={() => navigate('/conversation-type')}
          canUseTrainingPlans={trainingPlansEnabled}
          page={trainingPage}
          pages={trainingData.pages}
          onPageChange={setTrainingPage}
          loading={trainingLoading}
        />
      )}

      {view === 'training' && completedPlan && (
        <CompletedTrainingModal
          plan={completedPlan.plan}
          meeting={completedPlan.meeting}
          streak={completedPlan.streak}
          onClose={() => dispatch(clearCompletedPlan())}
        />
      )}

      {view === 'training' && (
        <SchedulePickerModal
          open={scheduler.open}
          title={selectedPlan?.scheduledTime ? 'Reschedule' : 'Schedule'}
          dateISO={scheduler.dateISO}
          timeHHmm={scheduler.timeHHmm}
          onChangeDate={(date) => dispatch(setScheduleDate(date))}
          onChangeTime={(time) => dispatch(setScheduleTime(time))}
          onClose={() => dispatch(closeScheduleModal())}
          onSave={async () => {
            try {
              const result = await dispatch(saveScheduleThunk());
              const when = result?.label || new Date(result.scheduledTime).toLocaleString();
              toast({
                title: 'Scheduled',
                description: `Training "${result.title}" set for ${when}.`,
              });
            } catch (error) {
              const messages = {
                MISSING: ['Pick date & time', 'Please choose both a date and a time.'],
                PAST: ['Invalid time', 'Please pick a future time.'],
              };
              const [title, description] = messages[error?.code] || [
                'Could not schedule',
                'Please try again.',
              ];
              toast({ title, description, variant: 'destructive' });
            }
          }}
          minDateISO={toISODate(new Date())}
        />
      )}
    </main>
  );
}
